import YoutubeArticleEmbed from "../components/YoutubeArticleEmbed";
import Nd1BlogArticleShell from "../nd1/components/Nd1BlogArticleShell";
import { APPLYING_FOR_JOBS_VIDEO_ID, youtubeEmbedUrl, youtubeThumbnailUrl, youtubeWatchUrl } from "../lib/youtubeVideos";
import { buildPublicPageUrl } from "../lib/pageSeo";
import { getPublicBlogPostByPath } from "../data/publicBlogPosts";

const PAGE_TITLE = "Applying for jobs is broken: How AI created a hiring mess";
const PAGE_PATH = "/blog/applying-for-jobs-is-broken";
const PAGE_DESCRIPTION =
  "From selective applications to mass AI spam — why job seekers and hiring teams are both exhausted, and what JOBUWIN is building instead.";

function ApplyingForJobsIsBrokenBlogPage() {
  const post = getPublicBlogPostByPath(PAGE_PATH);
  const videoJsonLd = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    embedUrl: youtubeEmbedUrl(APPLYING_FOR_JOBS_VIDEO_ID),
    contentUrl: youtubeWatchUrl(APPLYING_FOR_JOBS_VIDEO_ID),
    url: buildPublicPageUrl(PAGE_PATH),
    publisher: {
      "@type": "Organization",
      name: "JOBUWIN",
      url: buildPublicPageUrl("/"),
    },
  };

  return (
    <Nd1BlogArticleShell
      seo={{
        title: PAGE_TITLE,
        description: PAGE_DESCRIPTION,
        path: PAGE_PATH,
        canonicalPath: PAGE_PATH,
        type: "article",
        schemaType: "article",
        imagePath: youtubeThumbnailUrl(APPLYING_FOR_JOBS_VIDEO_ID),
        imageAlt: PAGE_TITLE,
        articlePublishedTime: post?.publishedDate,
        articleModifiedTime: post?.publishedDate,
        jsonLd: videoJsonLd,
      }}
      eyebrow="Video"
      title={PAGE_TITLE}
    >
      <YoutubeArticleEmbed
        videoId={APPLYING_FOR_JOBS_VIDEO_ID}
        title={PAGE_TITLE}
        className="nd1-article-video"
        playerClassName="nd1-article-video__player"
      />

      <p>
        A few years ago, maybe five or six years ago, before AI exploded into the hiring process, applying for jobs
        felt very different.
      </p>

      <p>
        Like many contractors and professionals, I was selective about the roles I applied for. I would spend time
        understanding the company, the people behind it, the problem they were trying to solve, and whether my
        experience genuinely matched what they needed. Sending a CV was intentional. You applied because you believed you
        could contribute something meaningful.
      </p>

      <p>
        And on the hiring side, companies would post a role and receive a manageable number of applications. Hiring
        managers and recruiters could realistically review CVs, identify good candidates, and have proper conversations
        with people. It was not perfect, but it still felt human.
      </p>

      <p>
        Fast forward to today, and the entire process feels like it has collapsed under the weight of automation.
      </p>

      <p>
        Like many of you, I have been actively applying for jobs recently, and I have seen firsthand the turmoil that
        artificial intelligence, automated job application tools, AI generated CVs, ATS systems, and automated filtering
        platforms have created in the hiring market.
      </p>

      <p>
        Candidates are now using AI to rewrite CVs, optimise keywords, generate cover letters, and mass apply to
        hundreds of jobs within minutes. At the same time, companies are overwhelmed by impossible volumes of
        applications, so they respond by introducing more automation, more filtering systems, more AI scoring, and more
        barriers between themselves and actual people.
      </p>

      <p>
        The result is a system where AI is effectively talking to AI while genuine human beings are disappearing
        somewhere in the middle.
      </p>

      <p>And honestly, nobody seems happy anymore.</p>

      <p>
        Applicants are exhausted and disheartened. Many highly capable people feel invisible because they know their
        application may never even reach a human being. They spend hours tailoring applications, rewriting CVs, and
        trying to guess what an algorithm wants to see rather than simply presenting themselves honestly.
      </p>

      <p>
        At the same time, hiring managers and recruiters are drowning in noise. They are receiving hundreds, sometimes
        thousands, of applications that all start looking the same because they were generated, optimised, or assisted
        by the exact same tools.
      </p>

      <p>The signal has been completely lost in the noise.</p>

      <h2>What makes this worse</h2>

      <p>
        The system is now actively encouraging behaviour that nobody actually believes in. Candidates no longer feel
        rewarded for authenticity. They feel rewarded for gaming algorithms. Companies no longer feel connected to
        candidates. They feel trapped managing volume.
      </p>

      <p>So everyone adds more technology to solve the problems created by the previous layer of technology.</p>

      <ul className="nd1-article-list">
        <li>AI creates job descriptions.</li>
        <li>AI rewrites CVs.</li>
        <li>AI mass applies to jobs.</li>
        <li>AI filters applications.</li>
        <li>AI ranks candidates.</li>
      </ul>

      <p>And somewhere in the middle of all of this, real people are struggling to find each other.</p>

      <p>
        As someone who has worked as a contractor for a long time, I have lived through hiring cycles, market
        fluctuations, economic uncertainty, and the constant process of re-entering the job market. One of the
        advantages of contracting is that you work with different companies, different teams, and different challenges.
        You meet brilliant people and constantly learn. But one of the realities of contracting is that you repeatedly
        re-enter the hiring market. You repeatedly go through the process of trying to communicate who you are, what
        you can do, and why you are the right fit for something.
      </p>

      <p>Until recently, that process still felt relatively natural.</p>

      <p>
        Now it feels industrialised. Overwhelming. Impersonal. Frustrating. Disheartening. At times, completely
        hopeless.
      </p>

      <p>It is not just me feeling this.</p>

      <p>
        LinkedIn is now littered with posts from both sides of the hiring process expressing frustration. Applicants are
        talking openly about feeling ignored, exhausted, and invisible. Hiring managers and recruiters are equally
        vocal about being overwhelmed by the sheer volume of applications and struggling to identify genuine candidates
        amongst the noise.
      </p>

      <p>
        Both sides are frustrated. Both sides feel the process is broken. And both sides increasingly distrust the
        system they are forced to use.
      </p>

      <p>
        Applicants think companies are impossible to reach. Companies think applicants are impossible to filter.
        Recruiters are overwhelmed. Hiring managers are fatigued. Nobody trusts the process anymore.
      </p>

      <p>That was the moment I started asking myself a bigger question: what is the actual solution here?</p>

      <p>
        Because adding more AI on top of AI does not seem to be fixing anything. It is simply accelerating the noise.
      </p>

      <p>And that is ultimately what led me down the path of building JOBUWIN.</p>

      <h2>Human again</h2>

      <p>
        Not because I wanted to build “another job platform”, but because I genuinely believe hiring needs to become
        human again.
      </p>

      <p>
        The idea behind JOBUWIN is actually very simple. Use technology to create genuine human connection instead of
        hiding people behind perfectly optimised documents and automated scoring systems.
      </p>

      <p>
        A hiring manager should be able to introduce themselves, explain the role, explain the company, and explain who
        they are genuinely looking for as a person. And applicants should have the opportunity to present themselves
        authentically as well, instead of endlessly trying to optimise keywords for algorithms they do not understand.
      </p>

      <p>The goal is not to create more volume. The goal is not to create more automation.</p>

      <p>The goal is not to help people spam applications faster.</p>

      <p>
        The goal is to reduce noise. Fewer applications. Better matches. More authenticity. More genuine interaction
        between real people.
      </p>

      <p>
        Because in a world rapidly accelerating toward AI driven automation, I think the future of hiring should not
        become AI versus AI.
      </p>

      <p>
        <strong>It should remain human to human.</strong>
      </p>

      <p className="nd1-article-cta">
        When you have a moment, have a look at{" "}
        <a href="https://www.jobuwin.com" rel="noopener noreferrer">
          www.jobuwin.com
        </a>
        .
      </p>
    </Nd1BlogArticleShell>
  );
}

export default ApplyingForJobsIsBrokenBlogPage;
